import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, ShoppingCart, Share2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ProductCard from '../components/product/ProductCard';
import ShareProduct from '../components/ShareProduct';
import SEOHead from '../components/SEOHead';
import ProductReviews from '../components/reviews/ProductReviews';
import { productsApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import { formatPrice, calculateDiscount } from '../utils/currency';
import { translateLabel } from '../utils/labels';
import { useWishlist } from '../context/WishlistContext';
import { useLocalization } from '../contexts/LocalizationContext';
import { useGeneralSettings } from '../contexts/GeneralSettingsContext';
import { getOptimizedImageUrl, getImageSrcSet, getImageSEO } from '../utils/imageOptimization';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { countryCode } = useLocalization();
  const { settings: generalSettings } = useGeneralSettings();
  // Reviews are ON by default; only hide when the admin explicitly disables them.
  const reviewsEnabled = generalSettings?.features?.reviewsEnabled !== false;
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

  // Mouse-hover zoom on the main product image — desktop only (there's
  // nothing sensible to "hover" on touch). Position is tracked as a
  // percentage of the image box so it works at any rendered size.
  const handleImageMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    setZoomPosition({ x, y });
  };

  const currentLang = i18n.language;

  useEffect(() => {
    if (countryCode) {
      fetchProduct();
      window.scrollTo(0, 0);
    }
  }, [slug, countryCode]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const data = await productsApi.getBySlug(slug, countryCode);
      setProduct(data);
      // Non-sized products get exactly one auto-created "Única" size
      // variant server-side — auto-select it so the selector stays hidden
      // and add-to-cart behaves exactly like a product with no sizing.
      // Real multi-size products start with nothing selected: the shopper
      // must make a deliberate choice (sizes can be priced/stocked
      // differently, so we never guess).
      const variants = data.sizeVariants || [];
      setSelectedSize(variants.length === 1 ? variants[0].size : null);

      if (data.id) {
        const related = await productsApi.getRelated(data.id, 4, countryCode);
        setRelatedProducts(related.products || []);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast({
        title: t('common.error'),
        description: 'Product not found',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleAddToCart = async () => {
    if (!product || isAddingToCart) return;
    if ((product.sizeVariants || []).length > 1 && !selectedSize) {
      toast({
        title: t('productDetailExtra.selectSize'),
        variant: 'destructive'
      });
      return;
    }
    setIsAddingToCart(true);
    try {
      // CartContext.addToCart handles its own success/error toasts and never
      // throws. Rely on its return value to know whether the item was really
      // persisted — otherwise we'd show a bogus success toast on network/API
      // failures (the "silent failure" bug where cart stays empty).
      const ok = await addToCart(product.id, quantity, selectedSize);
      if (ok) {
        setQuantity(1);
      }
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleToggleWishlist = async () => {
    if (!product) return;
    
    try {
      await toggleWishlist(product.id);
      const isWishlisted = isInWishlist(product.id);
      toast({
        title: t('common.success'),
        description: isWishlisted ? 'Added to wishlist' : 'Removed from wishlist'
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.message || 'Failed to update wishlist',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square bg-gray-200 animate-pulse rounded-lg" />
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 animate-pulse rounded w-3/4" />
              <div className="h-6 bg-gray-200 animate-pulse rounded w-1/4" />
              <div className="h-20 bg-gray-200 animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <SEOHead title={t('product.notFound')} />
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">{t('product.notFound')}</h2>
          <Link to="/shop">
            <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">{t('checkout.continueShopping')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  // With versioned schema, API returns flattened data for selected country
  // So product.name, product.description are already strings (not objects)
  const productName = product.name || '';
  const productDescription = product.description || '';
  const productShortDescription = product.shortDescription || '';
  const productHighlights = product.highlights || [];
  const images = product.images || [];
  const isWishlisted = isInWishlist(product.id);

  // Price and stock now live on the specific size variant, not the
  // product/version — each size can be priced and stocked completely
  // independently. `sizeVariants` comes straight from the storefront API.
  const sizeVariants = product.sizeVariants || [];
  const hasSizeChoice = sizeVariants.length > 1;
  const activeVariant = sizeVariants.find((sv) => sv.size === selectedSize) || null;

  // Before a size is chosen (only possible when there's more than one),
  // fall back to the product's derived summary price for display so the
  // page isn't blank — but stock/add-to-cart stay gated on a real choice.
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayCompareAtPrice = activeVariant ? activeVariant.compareAtPrice : product.compareAtPrice;

  const inv = activeVariant?.inventory || {};
  const trackInventory = Boolean(inv.trackInventory);
  const allowBackorder = Boolean(inv.allowBackorder);
  const availableStock = !activeVariant
    ? 0
    : (!trackInventory || allowBackorder
        ? Infinity
        : Math.max(0, (inv.quantity ?? 0) - (inv.reserved ?? 0)));
  const isOutOfStock = hasSizeChoice
    ? Boolean(activeVariant) && availableStock === 0
    : availableStock === 0;
  
  // Helper to get multilingual alt text
  const getImageAltText = (image, index = 0) => {
    if (!image?.alt) return `${productName} - Image ${index + 1}`;
    if (typeof image.alt === 'string') return image.alt;
    // Multilingual alt object
    return image.alt[currentLang] || image.alt.en || image.alt.es || `${productName} - Image ${index + 1}`;
  };

  const hasDiscount = displayCompareAtPrice && displayCompareAtPrice > displayPrice;
  const discountPercentage = hasDiscount
    ? calculateDiscount(displayCompareAtPrice, displayPrice)
    : 0;

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        title={productName}
        description={productShortDescription || productDescription?.substring(0, 160)}
        image={images[0]}
        imageAlt={getImageAltText(images[0], 0)}
        url={`/product/${product.slug}`}
        type="product"
        price={displayPrice?.toString()}
        currency={product.currency || 'USD'}
        availability={!isOutOfStock ? 'in stock' : 'out of stock'}
        // `product.brand` never existed on the schema (only the mostly-
        // unresolved `brandId` FK does) — this was always undefined,
        // which is exactly Search Console's "No global identifier
        // provided (e.g., gtin, brand)". CimonaMía is the seller/
        // redesigner of record for every piece regardless of the
        // original garment's manufacturer, same reasoning already used
        // for the Google Merchant Center feed (routes/merchant_feed.py).
        brand="CimonaMía"
        sku={product.sku}
      />
      
      <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8">
        <nav className="text-sm mb-6">
          <ol className="flex items-center gap-2">
            <li><Link to="/" className="text-gray-500 hover:text-gray-700">{t('common.home')}</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to="/shop" className="text-gray-500 hover:text-gray-700">{t('nav.shop')}</Link></li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-medium truncate max-w-xs">{productName}</li>
          </ol>
        </nav>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16">
          <div>
            {/* Wrapper (relative, no overflow clipping) so the magnified
                panel below can sit just outside the image box without
                being clipped by its own `overflow-hidden`. */}
            <div className="relative">
            <div
              className="relative aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-4 lg:cursor-zoom-in"
              onMouseEnter={() => setIsZooming(true)}
              onMouseLeave={() => setIsZooming(false)}
              onMouseMove={handleImageMouseMove}
            >
              {images.length > 0 ? (
                <img
                  src={getOptimizedImageUrl(images[selectedImage], 'large') || images[selectedImage]?.original || images[selectedImage]?.medium}
                  srcSet={getImageSrcSet(images[selectedImage])}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt={getImageAltText(images[selectedImage], selectedImage)}
                  title={productName}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No image available
                </div>
              )}
              
              <div className="absolute top-4 left-4 flex flex-col gap-2" data-testid="pdp-labels">
                {(() => {
                  // Admin-managed `labels` array — same convention/colors as
                  // ProductCard.jsx. Falls back to legacy boolean flags when
                  // no labels are set, so older products still show a badge.
                  const COLOR_TOKENS = {
                    terracotta: { bg: 'var(--terracotta)', fg: '#fefcf7' },
                    ink: { bg: 'var(--ink)', fg: '#fefcf7' },
                    accent: { bg: 'var(--accent, #e8d9c5)', fg: 'var(--ink)' },
                    sage: { bg: '#7a8b6f', fg: '#fefcf7' },
                    cream: { bg: '#fefcf7', fg: 'var(--ink)' },
                  };
                  const labels = Array.isArray(product.labels) ? product.labels : [];
                  if (labels.length) {
                    return labels.map((lbl, i) => {
                      const tone = COLOR_TOKENS[lbl.color] || COLOR_TOKENS.terracotta;
                      return (
                        <Badge
                          key={`${lbl.text}-${i}`}
                          style={{
                            background: tone.bg,
                            color: tone.fg,
                            fontSize: '10px',
                            letterSpacing: '2px',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                            padding: '4px 10px',
                            borderRadius: 0,
                            border: lbl.color === 'cream' ? '1px solid var(--line)' : 'none',
                          }}
                          data-testid={`pdp-label-${lbl.text.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {translateLabel(lbl.text, t)}
                        </Badge>
                      );
                    });
                  }
                  return (
                    <>
                      {product.isNew && (
                        <Badge className="bg-[#2C2420] text-white">NEW</Badge>
                      )}
                      {hasDiscount && (
                        <Badge className="bg-[#be1f1a] text-white">
                          {discountPercentage}% {t('products.off')}
                        </Badge>
                      )}
                      {product.isBestSelling && (
                        <Badge className="bg-amber-500 text-white">BEST SELLER</Badge>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* ReBorn badge — subtle mark for pieces made from reclaimed
                  materials, kept out of the way in the opposite corner.
                  Links through to the page explaining what the sello means. */}
              {product.isReborn && (
                <Link
                  to="/reborn"
                  className="absolute bottom-4 right-4 z-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src="/reborn-logo.png"
                    alt={t('products.rebornBadge')}
                    title={t('products.rebornBadge')}
                    className="w-20 h-auto rounded-md bg-[#FEFCF7] px-2 py-1.5 shadow-sm"
                    data-testid="pdp-reborn-badge"
                  />
                </Link>
              )}

              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage(prev => (prev - 1 + images.length) % images.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow-lg"
                  >
                    <ChevronLeft className="h-6 w-6 text-[#2C2420]" />
                  </button>
                  <button
                    onClick={() => setSelectedImage(prev => (prev + 1) % images.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow-lg"
                  >
                    <ChevronRight className="h-6 w-6 text-[#2C2420]" />
                  </button>
                </>
              )}

              {/* Lens — marks which part of the image the zoom panel is
                  currently showing. Desktop only, purely decorative. */}
              {isZooming && (
                <div
                  className="hidden lg:block absolute border-2 border-white shadow-md pointer-events-none"
                  style={{
                    width: '30%',
                    height: '30%',
                    left: `calc(${zoomPosition.x}% - 15%)`,
                    top: `calc(${zoomPosition.y}% - 15%)`,
                    background: 'rgba(255,255,255,0.15)',
                  }}
                />
              )}
            </div>

            {/* Magnified panel — floats just outside the image box so it
                never squeezes the layout; only shown on large screens
                where there's room for it beside the product info. Uses
                the original (highest-res) image so the zoomed-in result
                is actually sharp. */}
            {isZooming && images.length > 0 && (
              <div
                className="hidden lg:block absolute top-0 z-30 rounded-lg shadow-2xl border border-gray-200 bg-white pointer-events-none"
                style={{
                  left: 'calc(100% + 24px)',
                  width: '480px',
                  height: '100%',
                  backgroundImage: `url(${images[selectedImage]?.original || getOptimizedImageUrl(images[selectedImage], 'large')})`,
                  backgroundSize: '220%',
                  backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                  backgroundRepeat: 'no-repeat',
                }}
              />
            )}
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index
                        ? 'border-[#be1f1a]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={getOptimizedImageUrl(img, 'thumbnail') || img.thumbnail || img.medium}
                      alt={getImageAltText(img, index)}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            {product.category && (
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">
                {product.category}
              </p>
            )}

            <h1 className="text-3xl md:text-4xl font-serif text-[#2C2420] mb-4">
              {productName}
            </h1>

            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl font-bold text-[#be1f1a]">
                {formatPrice(displayPrice)}
              </span>
              {hasDiscount && (
                <span className="text-xl text-gray-400 line-through">
                  {formatPrice(displayCompareAtPrice)}
                </span>
              )}
            </div>

            {hasSizeChoice && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">
                  {t('products.size', { defaultValue: 'Talla' })}
                  {activeVariant && (
                    <span className="ml-2 font-normal text-gray-500">— {activeVariant.size}</span>
                  )}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {sizeVariants.map((sv) => {
                    const svInv = sv.inventory || {};
                    const svStock = !svInv.trackInventory || svInv.allowBackorder
                      ? Infinity
                      : Math.max(0, (svInv.quantity ?? 0) - (svInv.reserved ?? 0));
                    const svUnavailable = sv.isAvailable === false || svStock === 0;
                    const isSelected = selectedSize === sv.size;
                    return (
                      <button
                        key={sv.size}
                        type="button"
                        onClick={() => {
                          if (svUnavailable) return;
                          setSelectedSize(sv.size);
                          setQuantity(1);
                        }}
                        disabled={svUnavailable}
                        title={svUnavailable ? t('products.outOfStock') : undefined}
                        className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                          isSelected
                            ? 'border-[#be1f1a] bg-[#be1f1a] text-white'
                            : svUnavailable
                              ? 'border-gray-200 text-gray-300 line-through cursor-not-allowed'
                              : 'border-gray-300 hover:border-[#be1f1a] text-[#2C2420]'
                        }`}
                      >
                        {sv.size}
                      </button>
                    );
                  })}
                </div>
                {!activeVariant && (
                  <p className="text-sm text-gray-500 mt-2">
                    {t('productDetailExtra.selectSize')}
                  </p>
                )}
              </div>
            )}

            {/* Only one size exists — nothing to choose, but the shopper
                should still see what size they're actually buying. */}
            {!hasSizeChoice && activeVariant && activeVariant.size && activeVariant.size !== 'Única' && (
              <div className="mb-6">
                <h3 className="font-semibold mb-1">
                  {t('products.size', { defaultValue: 'Talla' })}
                  <span className="ml-2 font-normal text-gray-500">{activeVariant.size}</span>
                </h3>
              </div>
            )}

            {/* Medidas — only the fields the admin actually filled in for
                the selected size are shown; garments with no measurements
                entered render nothing here. */}
            {(() => {
              const m = activeVariant?.measurements;
              if (!m) return null;
              const FIELDS = [
                { key: 'bust', label: t('products.measurements.bust', { defaultValue: 'Busto' }) },
                { key: 'waist', label: t('products.measurements.waist', { defaultValue: 'Cintura' }) },
                { key: 'hip', label: t('products.measurements.hip', { defaultValue: 'Cadera' }) },
                { key: 'length', label: t('products.measurements.length', { defaultValue: 'Altura' }) },
              ].filter(({ key }) => m[key] !== null && m[key] !== undefined);
              if (!FIELDS.length) return null;
              return (
                <div className="mb-6" data-testid="pdp-measurements">
                  <h3 className="font-semibold mb-2">
                    {t('products.measurements.title', { defaultValue: 'Medidas' })}
                    <span className="ml-1 font-normal text-gray-400 text-sm">(cm)</span>
                  </h3>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-sm text-gray-700">
                    {FIELDS.map(({ key, label }) => (
                      <div key={key}>
                        <span className="text-gray-500">{label}:</span> {m[key]}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {productShortDescription && (
              <p className="text-gray-600 mb-6 leading-relaxed">
                {productShortDescription}
              </p>
            )}

            {productHighlights.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">{t('products.highlights')}</h3>
                <ul className="space-y-2">
                  {productHighlights.map((highlight, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#be1f1a] mr-2">•</span>
                      <span className="text-gray-700">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.sku && (
              <p className="text-sm text-gray-500 mb-6">SKU: {product.sku}</p>
            )}

            <div className="mb-6">
              <h3 className="font-semibold mb-3">{t('products.quantity')}:</h3>
              <div className="flex items-center border rounded-lg w-32">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={isOutOfStock || quantity <= 1}
                  className="px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  -
                </button>
                <span className="flex-1 text-center font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(availableStock, quantity + 1))}
                  disabled={isOutOfStock || quantity >= availableStock}
                  className="px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex gap-3 mb-6">
              <Button
                onClick={handleAddToCart}
                disabled={isOutOfStock || quantity > availableStock || isAddingToCart || (hasSizeChoice && !activeVariant)}
                data-testid="pdp-add-to-cart-btn"
                className="flex-1 bg-[#be1f1a] hover:bg-[#a71b17] disabled:bg-gray-300 disabled:cursor-not-allowed text-white h-12 text-base"
              >
                {isAddingToCart ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    {isOutOfStock ? t('products.outOfStock') : t('products.addToCart')}
                  </>
                )}
              </Button>
              <Button
                onClick={handleToggleWishlist}
                variant="outline"
                size="icon"
                className="h-12 w-12 border-2"
              >
                <Heart
                  className={`h-5 w-5 transition-colors ${
                    isWishlisted ? 'fill-[#be1f1a] text-[#be1f1a]' : 'text-gray-600'
                  }`}
                />
              </Button>
              <ShareProduct
                product={product}
                trigger={
                  <Button variant="outline" size="icon" className="h-12 w-12 border-2">
                    <Share2 className="h-5 w-5 text-gray-600" />
                  </Button>
                }
              />
            </div>

            {activeVariant && (
              <div className="mb-6">
                {isOutOfStock ? (
                  <p className="text-red-600 font-medium">{t('products.outOfStock')}</p>
                ) : trackInventory && !allowBackorder && Number.isFinite(availableStock) ? (
                  <p className="text-green-600 font-medium">
                    ✓ {t('products.inStock')} ({availableStock} {t('productDetailExtra.available')})
                  </p>
                ) : (
                  <p className="text-green-600 font-medium">
                    ✓ {t('products.inStock')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mb-16">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className={`w-full max-w-2xl mx-auto grid ${reviewsEnabled ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="description">{t('products.description')}</TabsTrigger>
              {reviewsEnabled && (
                <TabsTrigger value="reviews" data-testid="pdp-reviews-tab">{t('products.reviews')}</TabsTrigger>
              )}
              <TabsTrigger value="shipping">{t('shipping.shippingReturns')}</TabsTrigger>
            </TabsList>
            <TabsContent value="description" className="mt-6">
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">{productDescription}</p>
              </div>
            </TabsContent>
            {reviewsEnabled && (
              <TabsContent value="reviews" className="mt-6">
                <div className="max-w-4xl mx-auto">
                  <ProductReviews
                    productId={product.id}
                    productName={product.name}
                  />
                </div>
              </TabsContent>
            )}
            <TabsContent value="shipping" className="mt-6">
              <div className="prose max-w-none">
                <h3 className="text-lg font-semibold mb-3">{t('shipping.shippingInformation')}</h3>
                <p className="text-gray-700 mb-4">
                  {product.shippingInfo || t('shipping.defaultShippingInfo')}
                </p>
                <h3 className="text-lg font-semibold mb-3">{t('shipping.returnsPolicy')}</h3>
                <p className="text-gray-700">
                  {product.returnPolicy || t('shipping.defaultReturnPolicy')}
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl md:text-3xl font-serif text-[#2C2420] mb-6">{t('products.relatedProducts')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((product, idx) => (
                <ProductCard
                  key={product.id || product._id || product.slug || `related-${idx}`}
                  product={product}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProductDetailPage;
